import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import type { Candidate, Contact, District, Household, StreetSegment, Turf, UserRole } from '../db/types';
import {
  db,
  enqueueContact,
  enqueueFlag,
  enqueueHousehold,
  enqueueHouseholdPatch,
  flushOutbox,
  allPayloads,
  pendingFlags,
  pendingHouseholds,
  pendingHouseholdPatches,
  patchPendingHousehold,
  queuedCount,
  pruneSyncedOutbox,
  cacheContacts,
  cacheCandidates,
  cacheTurfData,
  reconcileHouseholds,
  cacheDistricts,
  cacheStreets,
  getCachedContacts,
  getCachedHouseholds,
  getCachedTurf,
  getCachedCandidates,
  getCachedDistricts,
  getCachedStreets,
  type ContactPayload,
  type FlagOp,
  type HouseholdPatchOp,
} from '../db/dexie';
import { deriveState, type ContactLike, type HouseholdState } from './model';
import { CONSENT_VERSION } from '../ui/support';
import { nearest, reverseGeocode, geocodeSearch, type GeoResult, type LatLng } from '../geo';
import { bboxFromBounds } from '../map/tileCache';

export interface Profile {
  id: string;
  role: UserRole;
  candidateId: string | null;
  email: string;
  name: string;
}

export interface RecordInput {
  householdId: string;
  support_level?: number | null;
  disposition?: string | null;
  candidate_slugs?: string[];
  issue_tags?: string[];
  wants_sign?: boolean;
  can_volunteer?: boolean;
  note?: string | null;
  geo?: LatLng | null;
}

interface AppData {
  ready: boolean;
  session: Session | null;
  profile: Profile | null;
  online: boolean;
  loading: boolean;
  error: string | null;
  candidates: Candidate[];
  candidateBySlug: Map<string, Candidate>;
  nameForSlug: (slug: string) => string;
  turf: Turf[];
  myTurf: Turf[];
  households: Household[];
  stateFor: (householdId: string) => HouseholdState;
  contactsFor: (householdId: string) => ContactLike[];
  queued: number;
  todayDone: number;
  todayPerHour: number;
  /** Coordinator/admin verify or reject a contact record (anti-fabrication).
   *  Resolves true only if the server accepted the change (else optimistic revert). */
  reviewContact: (contactId: string, ok: boolean) => Promise<boolean>;
  refresh: () => Promise<void>;
  syncNow: () => Promise<void>;
  recordContact: (input: RecordInput) => Promise<void>;
  setHouseholdFlags: (id: string, flags: { do_not_contact?: boolean; flagged_unsafe?: boolean }) => Promise<void>;
  nearestUnvisited: (from: LatLng | null, withinTurf?: string | null) => Household | null;
  /** Claim/lock a turf (anti double-knock). Idempotent for the same canvasser. */
  checkoutTurf: (turfId: string) => Promise<{ ok: boolean; conflict: boolean }>;
  releaseTurf: (turfId: string, completed: boolean) => Promise<void>;
  /** Find the nearest unvisited door AND lock its turf; returns conflict if held. */
  knockNearest: (from: LatLng | null) => Promise<{ doorId?: string; conflict?: string; none?: boolean }>;
  // ---- listless coverage ----
  districts: District[];
  streetSegments: StreetSegment[];
  myDistrict: District | null;
  streetsForDistrict: (districtId: string) => StreetSegment[];
  /** Create a household from a GPS/tapped point (write-local-first), return its id.
   *  Pass addressResolved to use a pre-fetched address (e.g. from a dragged pin). */
  knockHere: (
    geo: LatLng,
    opts?: { isMultiUnit?: boolean; unitCount?: number; streetSegmentId?: string | null; addressResolved?: string | null },
  ) => Promise<string | null>;
  /** Edit/confirm a household's address label (creator only, syncs offline-first). */
  setHouseholdAddress: (householdId: string, address: string) => Promise<void>;
  /** Mark a household as a multi-unit building with door + supporter counts. */
  setHouseholdMultiUnit: (
    householdId: string,
    fields: { is_multi_unit?: boolean; unit_count?: number | null; supporters_found?: number | null },
  ) => Promise<void>;
  /** Reverse-geocode a point to a suggested address (null if offline/unavailable). */
  suggestAddress: (geo: LatLng) => Promise<string | null>;
  /** Forward-geocode an address query → candidate points (scoped to the district). */
  searchAddress: (query: string) => Promise<GeoResult[]>;
  claimStreet: (segmentId: string) => Promise<{ ok: boolean; conflict: boolean }>;
  markStreetDone: (segmentId: string) => Promise<void>;
  releaseStreet: (segmentId: string) => Promise<void>;
  undoLast: () => Promise<boolean>;
}

const Ctx = createContext<AppData | null>(null);
export const useApp = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside provider');
  return v;
};

/** PostgREST caps each response at 1000 rows (db-max-rows), so a pre-loaded
 *  district (Fairfield ≈ 2.2k buildings) must be paged. Stable order by id keeps
 *  pages consistent. Throws on error (caller has an offline fallback). */
async function fetchAllRows<T>(table: string, columns: string): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let from = 0; from <= 100_000; from += PAGE) {
    const { data, error } = await supabase.from(table).select(columns).order('id', { ascending: true }).range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as T[]));
    if (!data || data.length < PAGE) break;
  }
  return out;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [turf, setTurf] = useState<Turf[]>([]);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [serverContacts, setServerContacts] = useState<Contact[]>([]);
  const [pending, setPending] = useState<ContactPayload[]>([]);
  const [flagOps, setFlagOps] = useState<FlagOp[]>([]);
  const [patchOps, setPatchOps] = useState<HouseholdPatchOp[]>([]);
  const [pendingHh, setPendingHh] = useState<Household[]>([]);
  const [queued, setQueued] = useState(0);
  const [districts, setDistricts] = useState<District[]>([]);
  const [streetSegments, setStreetSegments] = useState<StreetSegment[]>([]);
  const lastLocalId = useRef<number | null>(null);

  // ---- auth session ----
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // ---- online/offline ----
  useEffect(() => {
    const on = () => {
      setOnline(true);
      void syncNow();
    };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- profile when signed in ----
  useEffect(() => {
    if (!session) {
      setProfile(null);
      return;
    }
    void supabase
      .from('users')
      .select('id, role, candidate_id, name')
      .eq('id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        setProfile({
          id: session.user.id,
          email: session.user.email ?? '',
          role: (data?.role as UserRole) ?? 'canvasser',
          candidateId: data?.candidate_id ?? null,
          name: data?.name ?? session.user.email ?? 'You',
        });
      });
  }, [session]);

  const loadPending = useCallback(async () => {
    setPending(await allPayloads());
    setFlagOps(await pendingFlags());
    setPatchOps(await pendingHouseholdPatches());
    setPendingHh(await pendingHouseholds());
    setQueued(await queuedCount());
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (navigator.onLine) {
        // households + contacts are paged (a pre-loaded district can exceed the
        // 1000-row PostgREST cap); the rest are small single queries.
        const [c, t, hRows, ctRows, dd, ss] = await Promise.all([
          supabase.from('candidates').select('id,name,nation_slug,colour').order('name'),
          supabase.from('turf').select('id,name,geo_bounds,assigned_to,status,locked_at'),
          fetchAllRows<Household>(
            'households',
            'id,turf_id,address,lat,lng,elector_name,on_elector_list,address_omitted,do_not_contact,flagged_unsafe,district_id,street_segment_id,address_resolved,is_multi_unit,unit_count,supporters_found,total_units,created_by',
          ),
          fetchAllRows<Contact>(
            'contacts',
            'id,household_id,canvasser_id,candidate_id,support_level,disposition,candidate_tags,issue_tags,wants_sign,can_volunteer,consent_logged,consent_version,attempt_no,created_at,purge_after,client_uuid,note,review_ok',
          ),
          supabase.from('districts').select('id,name,geo_polygon,assigned_team,status,talking_points'),
          supabase
            .from('street_segments')
            .select('id,district_id,name,geo_line,status,claimed_by,claimed_at,last_activity_at'),
        ]);
        if (c.error || t.error || dd.error || ss.error)
          throw new Error(c.error?.message || t.error?.message || dd.error?.message || ss.error?.message);
        setCandidates((c.data ?? []) as Candidate[]);
        setTurf((t.data ?? []) as Turf[]);
        setHouseholds(hRows);
        setServerContacts(ctRows);
        setDistricts((dd.data ?? []) as District[]);
        setStreetSegments((ss.data ?? []) as StreetSegment[]);
        await Promise.all([
          cacheCandidates((c.data ?? []) as Candidate[]),
          cacheTurfData((t.data ?? []) as Turf[], []), // households reconciled below
          reconcileHouseholds(hRows), // drop stale cached pins (re-import/--reset), keep unsynced
          cacheContacts(ctRows),
          cacheDistricts((dd.data ?? []) as District[]),
          cacheStreets((ss.data ?? []) as StreetSegment[]),
        ]);
        const confirmed = new Set(ctRows.map((x) => x.client_uuid).filter(Boolean) as string[]);
        await pruneSyncedOutbox(confirmed, hRows);
      } else {
        // offline → hydrate from cache
        const [cc, t, h, ct, dd, ss] = await Promise.all([
          getCachedCandidates(),
          getCachedTurf(),
          getCachedHouseholds(),
          getCachedContacts(),
          getCachedDistricts(),
          getCachedStreets(),
        ]);
        setCandidates(cc);
        setTurf(t);
        setHouseholds(h);
        setServerContacts(ct);
        setDistricts(dd);
        setStreetSegments(ss);
      }
      await loadPending();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // best-effort offline fallback
      try {
        setCandidates(await getCachedCandidates());
        setTurf(await getCachedTurf());
        setHouseholds(await getCachedHouseholds());
        setServerContacts(await getCachedContacts());
        setDistricts(await getCachedDistricts());
        setStreetSegments(await getCachedStreets());
        await loadPending();
      } catch {
        /* ignore */
      }
    } finally {
      setLoading(false);
    }
  }, [loadPending]);

  useEffect(() => {
    if (profile) void refresh();
  }, [profile, refresh]);

  // Near-real-time: when a teammate's knock/claim streams in, refresh (debounced)
  // so shared pins + street status update live. RLS scopes what each user receives.
  useEffect(() => {
    if (!profile) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const bump = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (navigator.onLine) void refresh();
      }, 1500);
    };
    const ch = supabase
      .channel('field-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'households' }, bump)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts' }, bump)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'street_segments' }, bump)
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(ch);
    };
  }, [profile, refresh]);

  const syncNow = useCallback(async () => {
    await flushOutbox();
    await loadPending();
    if (navigator.onLine) await refresh();
  }, [loadPending, refresh]);

  // ---- merged contacts (server snapshot + not-yet-synced local), deduped ----
  const contactsByHousehold = useMemo(() => {
    const map = new Map<string, ContactLike[]>();
    const seen = new Set<string>();
    const add = (hid: string, c: ContactLike, key?: string | null) => {
      if (key) {
        if (seen.has(key)) return;
        seen.add(key);
      }
      const arr = map.get(hid) ?? [];
      arr.push(c);
      map.set(hid, arr);
    };
    for (const c of serverContacts) {
      add(c.household_id, c, c.client_uuid);
    }
    for (const p of pending) {
      add(p.household_id, {
        support_level: p.support_level,
        disposition: p.disposition,
        candidate_tags: (p.candidate_tags ?? {}) as ContactLike['candidate_tags'],
        issue_tags: (p.issue_tags ?? []) as string[],
        created_at: p.created_at,
        client_uuid: p.client_uuid,
      }, p.client_uuid);
    }
    return map;
  }, [serverContacts, pending]);

  const candidateBySlug = useMemo(() => new Map(candidates.map((c) => [c.nation_slug, c])), [candidates]);
  const nameForSlug = useCallback((slug: string) => candidateBySlug.get(slug)?.name ?? slug, [candidateBySlug]);

  // Households with not-yet-synced flag changes overlaid, so a DNC/unsafe flag
  // shows immediately and survives a refresh/force-quit before it syncs.
  const effHouseholds = useMemo(() => {
    // Union not-yet-synced GPS households so a knocked pin survives a refresh.
    const ids = new Set(households.map((h) => h.id));
    const base = pendingHh.length ? [...households, ...pendingHh.filter((h) => !ids.has(h.id))] : households;
    if (!flagOps.length && !patchOps.length) return base;
    const byId = new Map<string, Partial<Household>>();
    for (const op of flagOps) {
      const cur = byId.get(op.householdId) ?? {};
      if (op.do_not_contact != null) cur.do_not_contact = op.do_not_contact;
      if (op.flagged_unsafe != null) cur.flagged_unsafe = op.flagged_unsafe;
      byId.set(op.householdId, cur);
    }
    // Overlay not-yet-synced field edits (multi-unit counts, address) — later ops win.
    for (const op of patchOps) {
      byId.set(op.householdId, { ...(byId.get(op.householdId) ?? {}), ...op.fields });
    }
    return base.map((h) => (byId.has(h.id) ? { ...h, ...byId.get(h.id) } : h));
  }, [households, pendingHh, flagOps, patchOps]);

  const myTurf = useMemo(() => {
    if (!profile) return [];
    if (profile.role === 'coordinator' || profile.role === 'admin') return turf;
    return turf.filter((t) => t.assigned_to === profile.id);
  }, [turf, profile]);

  const stateFor = useCallback(
    (id: string) => deriveState(contactsByHousehold.get(id) ?? []),
    [contactsByHousehold],
  );
  const contactsFor = useCallback((id: string) => contactsByHousehold.get(id) ?? [], [contactsByHousehold]);

  // MY doors today + pace. Scoped to the current user: RLS gives staff the whole
  // team's contacts, so anchoring the rate on a teammate's earliest knock would
  // skew it badly — count only my own.
  const { todayDone, todayPerHour } = useMemo(() => {
    if (!profile) return { todayDone: 0, todayPerHour: 0 };
    const today = new Date().toISOString().slice(0, 10);
    const seen = new Set<string>();
    let n = 0;
    let earliest = '';
    const consider = (
      canvasserId: string | null | undefined,
      createdAt: string | null | undefined,
      key: string | null | undefined,
    ) => {
      if (canvasserId !== profile.id) return;
      if ((createdAt ?? '').slice(0, 10) !== today) return;
      if (key) {
        if (seen.has(key)) return;
        seen.add(key);
      }
      n++;
      if (!earliest || createdAt! < earliest) earliest = createdAt!;
    };
    for (const c of serverContacts) consider(c.canvasser_id, c.created_at, c.client_uuid);
    for (const p of pending) consider(p.canvasser_id, p.created_at, p.client_uuid);
    // doors/hour over the active session (since my first door today), floored at 15 min.
    const hours = earliest ? Math.max(0.25, (Date.now() - new Date(earliest).getTime()) / 3.6e6) : 0;
    return { todayDone: n, todayPerHour: hours > 0 ? Math.round(n / hours) : 0 };
  }, [serverContacts, pending, profile]);

  const recordContact = useCallback(
    async (input: RecordInput) => {
      if (!profile) return;
      const candidate_tags: Record<string, string[]> = {};
      for (const slug of input.candidate_slugs ?? []) candidate_tags[slug] = [];
      // PIPA: never store a location for a suppressed elector — it reconstructs the address.
      const omit = households.find((h) => h.id === input.householdId)?.address_omitted ?? false;
      const out = await enqueueContact({
        household_id: input.householdId,
        canvasser_id: profile.id,
        support_level: input.support_level ?? null,
        disposition: input.disposition ?? null,
        candidate_tags,
        issue_tags: input.issue_tags ?? [],
        wants_sign: input.wants_sign ?? false,
        can_volunteer: input.can_volunteer ?? false,
        consent_logged: true,
        consent_version: CONSENT_VERSION,
        note: input.note ?? null,
        geo_lat: omit ? null : (input.geo?.lat ?? null),
        geo_lng: omit ? null : (input.geo?.lng ?? null),
      });
      lastLocalId.current = out.localId ?? null;
      setPending((p) => [...p, out.payload]); // optimistic
      void flushOutbox().then(loadPending); // non-blocking sync
    },
    [profile, households, loadPending],
  );

  const undoLast = useCallback(async () => {
    const id = lastLocalId.current;
    if (id == null) return false;
    const row = await db.outbox.get(id);
    if (!row || row.syncedAt) return false; // already synced → can't undo locally
    await db.outbox.delete(id);
    lastLocalId.current = null;
    await loadPending();
    return true;
  }, [loadPending]);

  const setHouseholdFlags = useCallback(
    async (id: string, flags: { do_not_contact?: boolean; flagged_unsafe?: boolean }) => {
      // Write-local-first: queue the change + persist to the cache so a safety flag
      // survives airplane mode + force-quit, then flush via the (monotonic) RPC.
      await enqueueFlag(id, flags);
      setHouseholds((hs) => hs.map((h) => (h.id === id ? { ...h, ...flags } : h))); // optimistic
      await db.households.update(id, flags).catch(() => {});
      await loadPending();
      void flushOutbox().then(loadPending);
    },
    [loadPending],
  );

  const nearestUnvisited = useCallback(
    (from: LatLng | null, withinTurf?: string | null) => {
      const pool = effHouseholds.filter((h) => {
        if (h.do_not_contact) return false;
        if (withinTurf && h.turf_id !== withinTurf) return false;
        if (profile && profile.role === 'canvasser' && !myTurf.some((t) => t.id === h.turf_id)) return false;
        return !stateFor(h.id).visited;
      });
      return nearest(from, pool);
    },
    [effHouseholds, stateFor, myTurf, profile],
  );

  // Turf-locking. Track locally-claimed turf to avoid redundant RPC calls per door.
  const checkedOut = useRef<Set<string>>(new Set());
  const checkoutTurf = useCallback(async (turfId: string) => {
    if (checkedOut.current.has(turfId)) return { ok: true, conflict: false };
    if (!navigator.onLine) {
      // Can't claim a server lock offline; allow canvassing the already-assigned turf.
      checkedOut.current.add(turfId);
      return { ok: true, conflict: false };
    }
    const { error } = await supabase.rpc('checkout_turf', { p_turf_id: turfId });
    if (error) {
      const conflict = error.code === '55006' || /already checked out/i.test(error.message);
      if (!conflict) setError(error.message);
      return { ok: false, conflict };
    }
    checkedOut.current.add(turfId);
    void refresh(); // pick up the new locked_at/status
    return { ok: true, conflict: false };
  }, [refresh]);

  const releaseTurf = useCallback(async (turfId: string, completed: boolean) => {
    checkedOut.current.delete(turfId);
    if (!navigator.onLine) return;
    const { error } = await supabase.rpc('release_turf', { p_turf_id: turfId, p_completed: completed });
    if (error) setError(error.message);
    else void refresh();
  }, [refresh]);

  const knockNearest = useCallback(
    async (from: LatLng | null) => {
      const next = nearestUnvisited(from);
      if (!next) return { none: true };
      if (next.turf_id) {
        const r = await checkoutTurf(next.turf_id);
        if (r.conflict) return { conflict: 'That block is being canvassed by someone else.' };
      }
      return { doorId: next.id };
    },
    [nearestUnvisited, checkoutTurf],
  );

  // ---- listless coverage ----
  const myDistrict = useMemo(() => {
    if (!profile) return null;
    if (profile.candidateId) return districts.find((d) => d.assigned_team === profile.candidateId) ?? districts[0] ?? null;
    return districts[0] ?? null;
  }, [districts, profile]);

  const streetsForDistrict = useCallback(
    (districtId: string) => streetSegments.filter((s) => s.district_id === districtId),
    [streetSegments],
  );

  const knockHere = useCallback(
    async (
      geo: LatLng,
      opts?: { isMultiUnit?: boolean; unitCount?: number; streetSegmentId?: string | null; addressResolved?: string | null },
    ) => {
      if (!profile) return null;
      const district = myDistrict;
      if (!district) {
        setError('No district assigned — ask your coordinator.');
        return null;
      }
      const id = crypto.randomUUID();
      const presetAddr = opts?.addressResolved ?? null;
      const hh: Household = {
        id,
        turf_id: null,
        address: null,
        lat: geo.lat,
        lng: geo.lng,
        elector_name: null,
        on_elector_list: false,
        address_omitted: false,
        do_not_contact: false,
        flagged_unsafe: false,
        district_id: district.id,
        street_segment_id: opts?.streetSegmentId ?? null,
        address_resolved: presetAddr,
        is_multi_unit: opts?.isMultiUnit ?? false,
        unit_count: opts?.unitCount ?? null,
        supporters_found: null,
        created_by: profile.id,
      };
      // write-local-first: cache + outbox before any network
      await db.households.put(hh);
      await enqueueHousehold({
        id,
        district_id: district.id,
        street_segment_id: hh.street_segment_id ?? null,
        lat: geo.lat,
        lng: geo.lng,
        address: null,
        address_resolved: presetAddr,
        is_multi_unit: hh.is_multi_unit ?? false,
        unit_count: hh.unit_count ?? null,
        supporters_found: null,
        created_by: profile.id,
        created_at: new Date().toISOString(),
      });
      setHouseholds((hs) => [...hs, hh]); // optimistic — pin appears immediately
      await loadPending();
      void flushOutbox().then(loadPending);
      // If the caller already resolved the address (e.g. a dragged pin), skip the
      // lookup; otherwise reverse-geocode non-blocking so the volunteer never hunts.
      if (!presetAddr) {
        void reverseGeocode(geo.lat, geo.lng).then(async (addr) => {
          if (!addr) return;
          await patchPendingHousehold(id, { address_resolved: addr });
          setHouseholds((hs) => hs.map((x) => (x.id === id ? { ...x, address_resolved: addr } : x)));
          await enqueueHouseholdPatch(id, { address_resolved: addr });
          await loadPending();
          void flushOutbox().then(loadPending);
        });
      }
      return id;
    },
    [profile, myDistrict, loadPending],
  );

  const reviewContact = useCallback(async (contactId: string, ok: boolean): Promise<boolean> => {
    let prev: boolean | null = null;
    setServerContacts((cs) =>
      cs.map((c) => {
        if (c.id !== contactId) return c;
        prev = c.review_ok ?? null;
        return { ...c, review_ok: ok };
      }),
    );
    const { error } = await supabase.rpc('review_contact', { p_contact_id: contactId, p_ok: ok });
    if (error) {
      setError(error.message);
      setServerContacts((cs) => cs.map((c) => (c.id === contactId ? { ...c, review_ok: prev } : c))); // revert
      return false;
    }
    return true;
  }, []);

  const suggestAddress = useCallback((geo: LatLng) => reverseGeocode(geo.lat, geo.lng), []);

  const searchAddress = useCallback(
    (query: string) => {
      const b = myDistrict?.geo_polygon ? bboxFromBounds(myDistrict.geo_polygon) : null;
      const vb: [number, number, number, number] | undefined = b ? [b.west, b.south, b.east, b.north] : undefined;
      return geocodeSearch(b ? query : `${query}, Victoria BC`, vb);
    },
    [myDistrict],
  );

  // Write-local-first household edits. Both queue a durable, AUTHORIZED patch
  // (patch_household RPC) so the edit survives offline and is never silently
  // dropped by RLS for an imported/teammate household. patchPendingHousehold bakes
  // it into an unsynced GPS payload too, so the household syncs already-correct.
  const setHouseholdMultiUnit = useCallback(
    async (
      householdId: string,
      fields: { is_multi_unit?: boolean; unit_count?: number | null; supporters_found?: number | null },
    ) => {
      setHouseholds((hs) => hs.map((x) => (x.id === householdId ? { ...x, ...fields } : x)));
      await db.households.update(householdId, fields).catch(() => {});
      await patchPendingHousehold(householdId, fields);
      await enqueueHouseholdPatch(householdId, fields);
      await loadPending();
      void flushOutbox().then(loadPending);
    },
    [loadPending],
  );

  const setHouseholdAddress = useCallback(
    async (householdId: string, address: string) => {
      const a = address.trim() || null;
      setHouseholds((hs) => hs.map((x) => (x.id === householdId ? { ...x, address_resolved: a } : x)));
      await db.households.update(householdId, { address_resolved: a }).catch(() => {});
      await patchPendingHousehold(householdId, { address_resolved: a });
      await enqueueHouseholdPatch(householdId, { address_resolved: a });
      await loadPending();
      void flushOutbox().then(loadPending);
    },
    [loadPending],
  );

  const claimStreet = useCallback(
    async (segmentId: string) => {
      setStreetSegments((ss) =>
        ss.map((s) =>
          s.id === segmentId && profile
            ? { ...s, claimed_by: profile.id, claimed_at: new Date().toISOString(), status: s.status === 'untouched' ? 'in_progress' : s.status }
            : s,
        ),
      );
      const { error } = await supabase.rpc('claim_street', { p_segment_id: segmentId });
      if (error) {
        const conflict = error.code === '55006' || /claimed by/i.test(error.message);
        if (!conflict) setError(error.message);
        else void refresh(); // revert optimistic on conflict
        return { ok: !conflict, conflict };
      }
      void refresh();
      return { ok: true, conflict: false };
    },
    [profile, refresh],
  );

  const markStreetDone = useCallback(
    async (segmentId: string) => {
      setStreetSegments((ss) => ss.map((s) => (s.id === segmentId ? { ...s, status: 'done' } : s)));
      const { error } = await supabase.rpc('mark_street_done', { p_segment_id: segmentId });
      if (error) setError(error.message);
      else void refresh();
    },
    [refresh],
  );

  const releaseStreet = useCallback(
    async (segmentId: string) => {
      setStreetSegments((ss) =>
        ss.map((s) => (s.id === segmentId ? { ...s, claimed_by: null, status: s.status === 'in_progress' ? 'untouched' : s.status } : s)),
      );
      const { error } = await supabase.rpc('release_street', { p_segment_id: segmentId });
      if (error) setError(error.message);
      else void refresh();
    },
    [refresh],
  );

  const value: AppData = {
    ready,
    session,
    profile,
    online,
    loading,
    error,
    candidates,
    candidateBySlug,
    nameForSlug,
    turf,
    myTurf,
    households: effHouseholds,
    stateFor,
    contactsFor,
    queued,
    todayDone,
    todayPerHour,
    reviewContact,
    refresh,
    syncNow,
    recordContact,
    setHouseholdFlags,
    nearestUnvisited,
    checkoutTurf,
    releaseTurf,
    knockNearest,
    districts,
    streetSegments,
    myDistrict,
    streetsForDistrict,
    knockHere,
    suggestAddress,
    searchAddress,
    setHouseholdAddress,
    setHouseholdMultiUnit,
    claimStreet,
    markStreetDone,
    releaseStreet,
    undoLast,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
